import React, { useContext, useEffect, useMemo, useState } from 'react'

import type { PopupMenuProps, StyleClasses, MuiTheme as Theme } from '@paintscout/ui'
import { makeStyles, Tooltip, Button, Grid, MenuItem, PopupMenu, useMediaQuery } from '@paintscout/ui'

import {
  CLOSED_STATUSES,
  getObjectLabels,
  getQuoteNextPresentation,
  getQuoteOptions,
  hasIntegrationInterface
} from '@paintscout/util/builder'

import PrintIcon from '@material-ui/icons/PrintOutlined'
import DownloadIcon from '@material-ui/icons/PictureAsPdfOutlined'
import DeclineIcon from '@material-ui/icons/CancelOutlined'
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined'

import { usePresentationNav } from '../PresentationNavContext'
import { getPresentationButtonColors, hasTermsBlock } from '../../util'
// imported straight from the dialog, not the dialogs barrel — the barrel pulls the whole
// presentation editor in behind it, and this component renders in the customer view
import PresentationTermsDialog from '../../dialogs/PresentationTermsDialog'

import { QuoteContext } from '../../../context/QuoteContext'
import striptags from 'striptags'
import { useClientOptions, useUser } from '@paintscout/ui-business'

interface PresentationAcceptButtonStyleProps extends PresentationAcceptButtonProps {
  /** presentation accent colour — see getPresentationButtonColors */
  buttonBackgroundColor?: string
  /** contrast text for buttonBackgroundColor */
  buttonColor?: string
}

const useStyles = makeStyles<Theme, PresentationAcceptButtonStyleProps>(
  (theme: Theme) => {
    return {
      root: {},
      acceptGridRoot: {
        height: '100%',
        padding: theme.spacing(1, 0)
      },
      // the presentation theme never reaches the nav (it only becomes CSS for the content
      // subtree), so the accent colour is handed in as a prop instead
      button: ({ buttonBackgroundColor, buttonColor }: PresentationAcceptButtonStyleProps) => ({
        color: buttonColor,
        backgroundColor: buttonBackgroundColor,

        [theme.breakpoints.up('lg')]: {
          textAlign: 'center'
        },
        '&:hover': {
          backgroundColor: buttonBackgroundColor,
          color: buttonColor,
          opacity: 0.8
        }
      }),
      oversizeSplitButton: {
        padding: theme.spacing(2)
      },
      moreButton: {
        padding: 0,
        marginLeft: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
          marginLeft: theme.spacing(2)
        },
        [theme.breakpoints.down('xs')]: {
          marginLeft: theme.spacing(1)
        }
      },
      moreMenu: {
        ['@media print']: {
          display: 'none' // for printing from PresentationQuoteDrawer
        }
      },
      tooltip: {
        '& div': {
          fontSize: '.875rem'
        }
      }
    }
  },
  { name: 'PresentationAcceptButton' }
)

export interface PresentationAcceptButtonProps {
  classes?: StyleClasses<typeof useStyles>
  anchorOrigin?: PopupMenuProps['anchorOrigin']
  transformOrigin?: PopupMenuProps['transformOrigin']
}

export default function PresentationAcceptButton(props: PresentationAcceptButtonProps) {
  const { anchorOrigin, transformOrigin } = props

  const { options } = useClientOptions()

  const {
    view,
    isResponding,
    allowResponse,
    onAcceptClick,
    onPayClick,
    onMoreClick,
    isPreview,
    isPreviewAccept,
    onCloseCart,
    presentation: navPresentation
  } = usePresentationNav()
  const { quote } = useContext(QuoteContext)
  const { preferences } = useUser()

  const { allowPaymentsOnInvoices } = getQuoteOptions({ quote, options })

  // Stripe-only here missed Stripe W1D and Payrix accounts entirely, hiding Pay Now/Pay for any
  // account on either of those gateways (#22832) — the v1 PresentationAcceptButton already checks
  // all three.
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = (hasStripe || hasStripeW1d || hasPayrix) && !!onPayClick

  const allowPayment =
    hasPayments && allowPaymentsOnInvoices && quote.is_invoice && view !== 'work-order' && view !== 'product-order-form'

  const showAcceptButton =
    view !== 'work-order' && (allowResponse || isPreview) && !!onAcceptClick && !quote.is_invoice && !allowPayment

  // CLOSED_STATUSES is a general-purpose "terminal workflow status" list, but 'onHold' is meant to
  // be re-openable from the customer's own perspective — the v1 presentation's equivalent gate
  // (getAllowResponse in PresentationNavContextProvider.tsx) never blocked accept/decline for it
  // either. Treating it as closed here disabled Accept/Decline permanently until the estimate was
  // re-sent, even after a status change back to open (#22795).
  const hasOpenStatus = quote?.status?.value === 'onHold' || !CLOSED_STATUSES.includes(quote?.status?.value)

  const objectLabels = getObjectLabels({ options, quote })

  const smDown = useMediaQuery('sm')
  const xsDown = useMediaQuery('xs')

  const buttonLabel = getButtonLabel({ objectLabels, smDown })

  const hasMoreButton = !!onMoreClick

  // Decline makes no sense for an estimator previewing their own draft — exclude it once
  // onMoreClick is wired through the Preview/Accept dialog (#22699 follow-up).
  const allowDecline =
    allowResponse && options?.options?.allowDecline && hasOpenStatus && !quote.is_invoice && !isPreviewAccept

  const declineLabel = getDeclineLabel({ quote, objectLabels })
  const printLabel = getPrintLabel()
  const downloadLabel = getDownloadLabel()
  const moreButtonLabel = getMoreButtonLabel()

  // if the presentation already renders a terms block inline, don't duplicate it in this menu —
  // the quote drawer offers "View Terms" instead in that case (see PresentationQuoteDrawer)
  //
  // The nav context carries the presentation that is on screen, so it wins over the lookup, the
  // same way Presentation.tsx prefers its own `presentation` prop. A preview renders the quote's
  // snapshot or the builder's live Formik values, while `current: true` returns the company's saved
  // template — when only the rendered one holds the terms block, the menu offered "View Terms" on
  // top of terms already visible (#22699). The lookup stays as the fallback.
  const presentation = useMemo(
    () =>
      navPresentation ??
      getQuoteNextPresentation({ options, quote, current: true, isWorkOrder: view === 'work-order' }),
    [navPresentation, options, quote, view]
  )
  const hasTerms = !!striptags(quote.terms?.content) && !hasTermsBlock(presentation)

  const { backgroundColor, color } = getPresentationButtonColors(presentation?.advanced?.style)
  const classes = useStyles({ ...props, buttonBackgroundColor: backgroundColor, buttonColor: color })

  // "View Terms" is handled here rather than through onMoreClick: the item only shows when the
  // presentation has no terms block, so the terms are either inside the Quick Cart drawer's own
  // scroll pane or not on screen at all. A dialog covers both; scrolling to #terms covers neither.
  const [termsOpen, setTermsOpen] = useState(false)

  // the quote can update while the customer sits on the page, so hasTerms can flip to false with
  // the dialog open — close it rather than leaving termsOpen stale for the next mount
  useEffect(() => {
    if (!hasTerms) setTermsOpen(false)
  }, [hasTerms])

  const handleAcceptClick = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onAcceptClick(ev)
  }

  const [initStatus, _setInitStatus] = useState(quote?.status?.value)

  useEffect(() => {
    if (!CLOSED_STATUSES.includes(initStatus) && !hasOpenStatus) {
      onCloseCart()
    }
  }, [quote?.status?.value])

  const moreButton = (
    <Grid item>
      <PopupMenu
        className={classes.moreMenu}
        anchorOrigin={
          anchorOrigin || {
            vertical: 'top',
            horizontal: 'left'
          }
        }
        transformOrigin={
          transformOrigin || {
            vertical: 'bottom',
            horizontal: 'left'
          }
        }
        component={
          <Button variant="text" className={classes.moreButton}>
            {moreButtonLabel}
          </Button>
        }
      >
        <>
          {hasTerms && (
            <MenuItem
              icon={ListAltOutlinedIcon}
              onClick={() => {
                setTermsOpen(true)
              }}
            >
              View Terms
            </MenuItem>
          )}
          <MenuItem
            icon={PrintIcon}
            onClick={(ev) => {
              onMoreClick(ev, 'print')
            }}
          >
            {printLabel}
          </MenuItem>
          {/* Preview/Accept's onMoreClick only implements 'print' — the preview quote may be
              unsaved, and download needs a persisted _id/_rev, so the item is hidden here rather
              than left as a click that goes nowhere (#22699 follow-up). */}
          {!isPreviewAccept && (
            <MenuItem
              icon={DownloadIcon}
              onClick={(ev) => {
                onMoreClick(ev, 'download')
              }}
            >
              {downloadLabel}
            </MenuItem>
          )}
          {allowDecline && (
            <MenuItem
              icon={DeclineIcon}
              onClick={(ev) => {
                onMoreClick(ev, 'decline')
              }}
            >
              {declineLabel}
            </MenuItem>
          )}
        </>
      </PopupMenu>
    </Grid>
  )

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        wrap={'nowrap'}
        direction={'row'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        classes={{ container: classes.acceptGridRoot }}
      >
        {allowPayment && (
          <>
            <Grid item>
              <Tooltip
                classes={{ popper: classes.tooltip }}
                title={
                  quote.totals.balance_due > 0 && quote.totals.balance_due < 0.5
                    ? 'Payments are only available if the remaining balance is at least $0.50 '
                    : ''
                }
              >
                <div>
                  <Button
                    classes={{ root: classes.button }}
                    disabled={quote.totals.balance_due < 0.5}
                    loading={isResponding}
                    onClick={() => onPayClick()}
                  >
                    {getPaymentButtonLabel()}
                  </Button>
                </div>
              </Tooltip>
            </Grid>
            {hasMoreButton && moreButton}
          </>
        )}
        {showAcceptButton && (
          <>
            <Grid item>
              <Button
                loading={isResponding}
                disabled={!allowResponse || !hasOpenStatus}
                classes={{ root: classes.button }}
                onClick={(ev) => handleAcceptClick(ev)}
              >
                {buttonLabel}
              </Button>
            </Grid>
            {hasMoreButton && moreButton}
          </>
        )}
      </Grid>
      {/* outside moreButton on purpose — moreButton is rendered in two branches above */}
      {hasTerms && <PresentationTermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />}
    </div>
  )

  function getMoreButtonLabel() {
    if (xsDown) return 'More...'
    return 'More Actions...'
  }

  function getPrintLabel() {
    if (view === 'work-order') {
      return `Print ${objectLabels.workOrder.value}`
    } else if (quote?.is_invoice) {
      return `Print Invoice`
    } else {
      return `Print ${objectLabels.quote.value}`
    }
  }

  function getDownloadLabel() {
    if (view === 'work-order') {
      return `Download ${objectLabels.workOrder.value}`
    } else if (quote?.is_invoice) {
      return `Download Invoice`
    } else {
      return `Download ${objectLabels.quote.value}`
    }
  }

  function getDeclineLabel({ quote, objectLabels }) {
    if (objectLabels.declineButton) {
      return objectLabels.declineButton.value
    } else {
      const item =
        quote?.is_invoice && quote?.totals?.pending ? objectLabels.additionalWork.value : objectLabels.quote.value
      return `Decline ${item}`
    }
  }

  function getPaymentButtonLabel() {
    if (xsDown) return quote.totals.balance_due === 0 && quote.payments.length > 0 ? 'Paid' : 'Pay'
    return quote.totals.balance_due === 0
      ? quote.payments.length === 0
        ? 'No Balance Due'
        : 'Payment Received'
      : quote.totals.balance_due < 200
        ? 'Pay Balance'
        : 'Make Payment'
  }

  function getButtonLabel({ objectLabels, smDown }) {
    if (objectLabels.acceptButton) {
      return objectLabels.acceptButton.value
    } else if (smDown) {
      return 'Accept'
    } else {
      const item = objectLabels.quote.value
      return `Accept ${item}`
    }
  }
}
